/**
 * Metro 2 Eligibility Triage Component
 * 
 * 6-bucket AI triage system:
 * 1. AI Recommended for Metro 2
 * 2. Metro 2 Eligible
 * 3. Better for Factual Dispute
 * 4. Inquiry / Personal Info / Data Breach Flow Required
 * 5. Evidence Needed
 * 6. Not Recommended
 */

import React from 'react';
import {
  Sparkles,
  CheckCircle,
  AlertTriangle,
  FileText,
  Eye,
  Shield,
  XCircle,
  ExternalLink,
  ArrowRight,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import Metro2AccountCard from './Metro2AccountCard';

// 6-bucket configuration
const BUCKET_CONFIG = {
  ai_recommended: {
    key: 'ai_recommended',
    title: 'AI Recommended for Metro 2',
    icon: Sparkles,
    color: 'emerald',
    bgClass: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderClass: 'border-emerald-200 dark:border-emerald-700',
    textClass: 'text-emerald-700 dark:text-emerald-300',
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    description: 'Confirmed Metro 2 technical violations detected. Strongest candidates for Metro 2 dispute.',
    action: 'Select for Metro 2',
    actionVariant: 'default',
  },
  metro2_eligible: {
    key: 'metro2_eligible',
    title: 'Metro 2 Eligible',
    icon: CheckCircle,
    color: 'emerald',
    bgClass: 'bg-emerald-50/50 dark:bg-emerald-900/10',
    borderClass: 'border-emerald-200 dark:border-emerald-700/50',
    textClass: 'text-emerald-700 dark:text-emerald-300',
    iconClass: 'text-emerald-600 dark:text-emerald-400',
    description: 'Bureau differences detected that may indicate Metro 2 technical conflicts. Review recommended.',
    action: 'Select for Metro 2',
    actionVariant: 'outline',
  },
  factual_eligible: {
    key: 'factual_eligible',
    title: 'Better for Factual Dispute',
    icon: FileText,
    color: 'blue',
    bgClass: 'bg-blue-50 dark:bg-blue-900/20',
    borderClass: 'border-blue-200 dark:border-blue-700',
    textClass: 'text-blue-700 dark:text-blue-300',
    iconClass: 'text-blue-600 dark:text-blue-400',
    description: 'These items are disputable but lack Metro 2 technical conflicts. Better suited for Factual Dispute.',
    action: 'Save to Factual Queue',
    actionVariant: 'outline',
  },
  other_flow: {
    key: 'other_flow',
    title: 'Inquiry / Personal Info / Data Breach Flow Required',
    icon: ExternalLink,
    color: 'purple',
    bgClass: 'bg-purple-50 dark:bg-purple-900/20',
    borderClass: 'border-purple-200 dark:border-purple-700',
    textClass: 'text-purple-700 dark:text-purple-300',
    iconClass: 'text-purple-600 dark:text-purple-400',
    description: 'These items must be disputed through their dedicated flows, not Metro 2.',
    action: 'Route to Correct Flow',
    actionVariant: 'outline',
  },
  evidence_needed: {
    key: 'evidence_needed',
    title: 'Evidence Needed',
    icon: FileText,
    color: 'amber',
    bgClass: 'bg-amber-50 dark:bg-amber-900/20',
    borderClass: 'border-amber-200 dark:border-amber-700',
    textClass: 'text-amber-700 dark:text-amber-300',
    iconClass: 'text-amber-600 dark:text-amber-400',
    description: 'These items need supporting documentation before Metro 2 dispute can proceed.',
    action: 'Request Evidence',
    actionVariant: 'outline',
  },
  not_recommended: {
    key: 'not_recommended',
    title: 'Not Recommended',
    icon: XCircle,
    color: 'stone',
    bgClass: 'bg-stone-50 dark:bg-stone-800/50',
    borderClass: 'border-stone-200 dark:border-stone-700',
    textClass: 'text-stone-600 dark:text-stone-400',
    iconClass: 'text-stone-500 dark:text-stone-500',
    description: 'No clear dispute path identified for these items.',
    action: 'View Reason',
    actionVariant: 'ghost',
  },
};

// AI Recommendation Panel Component
const AIRecommendationPanel = ({ triageResult, onSelectAIRecommended, onSelectAllEligible, onViewRouted, onContinue, selectedItems = [] }) => {
  const totalReviewed = triageResult?.total_items_reviewed || 0;
  const aiRecommended = (triageResult?.confirmed_metro2_items?.length || 0);
  // Metro 2 Eligible includes needs_review + metro2_eligible (NOT AI Recommended to avoid double-count)
  const metro2EligibleOnly = (triageResult?.needs_metro2_review_items?.length || 0) + (triageResult?.metro2_eligible_items?.length || 0);
  // Total Metro 2 Eligible = AI Recommended + other eligible
  const totalMetro2Eligible = aiRecommended + metro2EligibleOnly;
  const evidenceNeeded = triageResult?.evidence_needed_items?.length || 0;
  const factualEligible = triageResult?.factual_eligible_items?.length || 0;
  const routedOutside = (triageResult?.inquiry_items?.length || 0) + 
                        (triageResult?.personal_info_items?.length || 0) + 
                        (triageResult?.data_breach_candidates?.length || 0) +
                        (triageResult?.other_flow_items?.length || 0);

  // Check if all eligible items are already selected
  const selectedCount = selectedItems.filter(i => i.selected).length;
  const allEligibleSelected = selectedCount >= totalMetro2Eligible && totalMetro2Eligible > 0;

  return (
    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 border-emerald-200 dark:border-emerald-700/50" data-testid="ai-recommendation-panel">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-800/50">
            <Sparkles className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <CardTitle className="text-lg text-emerald-900 dark:text-emerald-200">AI Recommendation</CardTitle>
        </div>
        <CardDescription className="text-emerald-700 dark:text-emerald-400">
          AI reviewed your report for field-level Metro 2 reporting issues. Items recommended for Metro 2 
          contain technical reporting patterns that may require verification. Items outside Metro 2 may 
          still be disputable, but they belong in another flow.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white/80 dark:bg-stone-900/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-stone-900 dark:text-white">{totalReviewed}</div>
            <div className="text-xs text-stone-500 dark:text-stone-400">Total Reviewed</div>
          </div>
          <div className="bg-emerald-100/80 dark:bg-emerald-900/50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{aiRecommended}</div>
            <div className="text-xs text-emerald-600 dark:text-emerald-400">AI Recommended</div>
          </div>
          <div className="bg-emerald-50/80 dark:bg-emerald-900/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{totalMetro2Eligible}</div>
            <div className="text-xs text-emerald-500 dark:text-emerald-500">Total Metro 2 Eligible</div>
          </div>
          <div className="bg-amber-50/80 dark:bg-amber-900/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{evidenceNeeded}</div>
            <div className="text-xs text-amber-500 dark:text-amber-500">Evidence Needed</div>
          </div>
          <div className="bg-blue-50/80 dark:bg-blue-900/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{factualEligible}</div>
            <div className="text-xs text-blue-500 dark:text-blue-500">Better for Factual</div>
          </div>
          <div className="bg-purple-50/80 dark:bg-purple-900/30 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{routedOutside}</div>
            <div className="text-xs text-purple-500 dark:text-purple-500">Routed Outside</div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2">
          {aiRecommended > 0 && (
            <Button onClick={onSelectAIRecommended} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="select-ai-recommended-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Select AI Recommended Items
            </Button>
          )}
          {totalMetro2Eligible > 0 && (
            <Button 
              variant="outline" 
              onClick={onSelectAllEligible} 
              className={`border-emerald-300 dark:border-emerald-600 ${
                allEligibleSelected 
                  ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300' 
                  : 'text-emerald-700 dark:text-emerald-300'
              }`}
              disabled={allEligibleSelected}
              data-testid="select-all-eligible-btn"
            >
              {allEligibleSelected ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  All Eligible Items Selected
                </>
              ) : (
                'Select All Eligible Items'
              )}
            </Button>
          )}
          {routedOutside > 0 && (
            <Button variant="ghost" onClick={onViewRouted} className="text-purple-600 dark:text-purple-400" data-testid="view-routed-btn">
              <ExternalLink className="w-4 h-4 mr-2" />
              View Routed Outside Metro 2
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Triage Bucket Component
const TriageBucket = ({ 
  bucketKey, 
  items, 
  selectedItems, 
  onItemSelect, 
  onBucketAction,
  expandedByDefault = false 
}) => {
  const [expanded, setExpanded] = React.useState(expandedByDefault);
  const config = BUCKET_CONFIG[bucketKey];
  const IconComponent = config.icon;

  if (!items || items.length === 0) return null;

  return (
    <div className={`rounded-xl border ${config.borderClass} ${config.bgClass} overflow-hidden`} data-testid={`triage-bucket-${bucketKey}`}>
      {/* Bucket Header */}
      <div 
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-white/80 dark:bg-stone-900/50`}>
            <IconComponent className={`w-5 h-5 ${config.iconClass}`} />
          </div>
          <div>
            <h3 className={`font-semibold ${config.textClass}`}>
              {config.title} ({items.length})
            </h3>
            <p className="text-sm text-stone-500 dark:text-stone-400 max-w-xl">
              {config.description}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={config.actionVariant}
            size="sm"
            onClick={(e) => { e.stopPropagation(); onBucketAction(bucketKey, items); }}
            className={config.actionVariant === 'default' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
          >
            {config.action}
          </Button>
          <Badge variant="outline" className={`${config.textClass} border-current`}>
            {items.length}
          </Badge>
        </div>
      </div>

      {/* Bucket Content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {items.map((item) => (
            <Metro2AccountCard
              key={item.item_id}
              account={item}
              selected={selectedItems.some(s => s.item_id === item.item_id && s.selected)}
              onSelect={() => onItemSelect(item.item_id)}
              showCheckbox={bucketKey === 'ai_recommended' || bucketKey === 'metro2_eligible'}
              compact={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Bureau Match Notice
const BureauMatchNotice = ({ triageResult }) => {
  const totalDifferences = triageResult?.total_bureau_differences_detected || 0;
  const hasUnresolved = triageResult?.has_unresolved_bureau_differences || false;

  if (totalDifferences === 0 && !hasUnresolved) return null;

  return (
    <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700" data-testid="bureau-match-notice">
      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
      <AlertTitle className="text-amber-700 dark:text-amber-300">
        {totalDifferences} Bureau-Level Differences Detected
      </AlertTitle>
      <AlertDescription className="text-amber-600 dark:text-amber-400">
        {triageResult?.user_message || 
          'Static fields matching across bureaus does not mean the account is clean. Metro 2 field-logic review found potential reporting issues requiring verification.'}
      </AlertDescription>
    </Alert>
  );
};

// No Metro 2 Items Warning
const NoMetro2Warning = ({ onStartFactual, onViewReason }) => (
  <Alert className="bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600" data-testid="no-metro2-warning">
    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
    <AlertTitle className="text-amber-700 dark:text-amber-400">No Metro 2 Technical Disputes Detected</AlertTitle>
    <AlertDescription className="text-amber-600 dark:text-amber-300">
      No Metro 2 technical disputes were detected for the selected items. These items may still 
      be disputable, but they belong in another dispute flow.
      <div className="flex gap-2 mt-3">
        <Button variant="outline" size="sm" onClick={onStartFactual}>
          Start Factual Dispute with These Items
        </Button>
        <Button variant="ghost" size="sm" onClick={onViewReason}>
          View Why Not Metro 2
        </Button>
      </div>
    </AlertDescription>
  </Alert>
);

// Selection Summary Bar
const SelectionSummaryBar = ({ selectedCount, factualCount, onContinue }) => (
  <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-600" data-testid="selection-summary">
    <CardContent className="py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span className="font-medium text-emerald-700 dark:text-emerald-300">
            {selectedCount} item(s) selected for Metro 2 review
          </span>
          {factualCount > 0 && (
            <span className="text-sm text-amber-600 dark:text-amber-400">
              +{factualCount} saved for Factual Dispute
            </span>
          )}
        </div>
        <Button onClick={onContinue} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="continue-selected-btn">
          Continue with Selected Items
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Mixed Selection Warning Modal
const MixedSelectionWarning = ({ metro2Count, nonMetro2Count, onContinue, onReview, onSendToFactual, onRemove }) => (
  <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-600" data-testid="mixed-selection-warning">
    <CardHeader>
      <CardTitle className="text-amber-800 dark:text-amber-300 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" />
        Mixed Selection Detected
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <p className="text-amber-700 dark:text-amber-400">
        {metro2Count} items will continue in Metro 2. {nonMetro2Count} items were saved for other 
        dispute flows because they do not contain Metro 2 technical conflicts.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onContinue} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          Continue with Metro 2 Eligible Items
        </Button>
        <Button variant="outline" onClick={onReview}>
          Review Routed Items
        </Button>
        <Button variant="outline" onClick={onSendToFactual}>
          Send Non-Metro 2 Items to Factual Queue
        </Button>
        <Button variant="ghost" onClick={onRemove}>
          Remove Non-Metro 2 Items
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Main Metro 2 Eligibility Triage Component
const Metro2EligibilityTriage = ({
  triageResult,
  loading,
  selectedItems,
  onItemSelect,
  onSelectAIRecommended,
  onSelectAllEligible,
  onContinue,
  onNavigateToFactual,
  onNavigateToInquiry,
  onNavigateToPI,
}) => {
  const [showRoutedOutside, setShowRoutedOutside] = React.useState(false);

  // Organize items into 6 buckets
  const buckets = React.useMemo(() => {
    if (!triageResult) return {};

    return {
      ai_recommended: triageResult.confirmed_metro2_items || [],
      metro2_eligible: [
        ...(triageResult.needs_metro2_review_items || []),
        ...(triageResult.metro2_eligible_items || []).filter(
          item => !triageResult.confirmed_metro2_items?.some(c => c.item_id === item.item_id)
        ),
      ],
      factual_eligible: triageResult.factual_eligible_items || [],
      other_flow: [
        ...(triageResult.inquiry_items || []),
        ...(triageResult.personal_info_items || []),
        ...(triageResult.data_breach_candidates || []),
        ...(triageResult.other_flow_items || []),
      ],
      evidence_needed: triageResult.evidence_needed_items || [],
      not_recommended: [
        ...(triageResult.not_recommended_items || []),
        ...(triageResult.normal_variation_items || []),
      ],
    };
  }, [triageResult]);

  const handleBucketAction = (bucketKey, items) => {
    switch (bucketKey) {
      case 'ai_recommended':
      case 'metro2_eligible':
        // Select all items in this bucket
        items.forEach(item => {
          if (!selectedItems.some(s => s.item_id === item.item_id && s.selected)) {
            onItemSelect(item.item_id);
          }
        });
        break;
      case 'factual_eligible':
        onNavigateToFactual?.();
        break;
      case 'other_flow':
        setShowRoutedOutside(true);
        break;
      case 'evidence_needed':
        // Show evidence upload modal
        break;
      case 'not_recommended':
        // Show reason modal
        break;
      default:
        break;
    }
  };

  const selectedCount = selectedItems.filter(i => i.selected).length;
  const factualCount = buckets.factual_eligible?.length || 0;
  const totalMetro2Eligible = (buckets.ai_recommended?.length || 0) + (buckets.metro2_eligible?.length || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
        <span className="ml-4 text-stone-500 dark:text-stone-400">Analyzing items for Metro 2 eligibility...</span>
      </div>
    );
  }

  if (!triageResult) {
    return (
      <div className="text-center py-16">
        <Info className="w-12 h-12 text-stone-400 dark:text-stone-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-stone-600 dark:text-stone-400">No Triage Data</h3>
        <p className="text-sm text-stone-500 dark:text-stone-500 mt-2">
          Upload a credit report to analyze items for Metro 2 eligibility.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="metro2-eligibility-triage">
      {/* AI Recommendation Panel */}
      <AIRecommendationPanel
        triageResult={triageResult}
        onSelectAIRecommended={onSelectAIRecommended}
        onSelectAllEligible={onSelectAllEligible}
        onViewRouted={() => setShowRoutedOutside(true)}
        onContinue={onContinue}
        selectedItems={selectedItems}
      />

      {/* Bureau Match Notice */}
      <BureauMatchNotice triageResult={triageResult} />

      {/* No Metro 2 Warning if applicable */}
      {totalMetro2Eligible === 0 && (
        <NoMetro2Warning 
          onStartFactual={onNavigateToFactual} 
          onViewReason={() => {}} 
        />
      )}

      {/* Triage Buckets */}
      <div className="space-y-4">
        {/* AI Recommended - Expanded by default */}
        <TriageBucket
          bucketKey="ai_recommended"
          items={buckets.ai_recommended}
          selectedItems={selectedItems}
          onItemSelect={onItemSelect}
          onBucketAction={handleBucketAction}
          expandedByDefault={true}
        />

        {/* Metro 2 Eligible */}
        <TriageBucket
          bucketKey="metro2_eligible"
          items={buckets.metro2_eligible}
          selectedItems={selectedItems}
          onItemSelect={onItemSelect}
          onBucketAction={handleBucketAction}
          expandedByDefault={buckets.ai_recommended?.length === 0}
        />

        {/* Better for Factual */}
        <TriageBucket
          bucketKey="factual_eligible"
          items={buckets.factual_eligible}
          selectedItems={selectedItems}
          onItemSelect={onItemSelect}
          onBucketAction={handleBucketAction}
        />

        {/* Evidence Needed */}
        <TriageBucket
          bucketKey="evidence_needed"
          items={buckets.evidence_needed}
          selectedItems={selectedItems}
          onItemSelect={onItemSelect}
          onBucketAction={handleBucketAction}
        />

        {/* Other Flows */}
        <TriageBucket
          bucketKey="other_flow"
          items={buckets.other_flow}
          selectedItems={selectedItems}
          onItemSelect={onItemSelect}
          onBucketAction={handleBucketAction}
        />

        {/* Not Recommended */}
        <TriageBucket
          bucketKey="not_recommended"
          items={buckets.not_recommended}
          selectedItems={selectedItems}
          onItemSelect={onItemSelect}
          onBucketAction={handleBucketAction}
        />
      </div>

      {/* Selection Summary */}
      {selectedCount > 0 && (
        <SelectionSummaryBar
          selectedCount={selectedCount}
          factualCount={factualCount}
          onContinue={onContinue}
        />
      )}
    </div>
  );
};

export default Metro2EligibilityTriage;
export { 
  AIRecommendationPanel, 
  TriageBucket, 
  BureauMatchNotice, 
  NoMetro2Warning, 
  SelectionSummaryBar,
  MixedSelectionWarning,
  BUCKET_CONFIG 
};
